import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Container, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import SettingsIcon from '@mui/icons-material/Settings';
import SensorsIcon from '@mui/icons-material/Sensors';
import MemoryIcon from '@mui/icons-material/Memory';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import ImageIcon from '@mui/icons-material/Image';
import BarChartIcon from '@mui/icons-material/BarChart';
import PolicyIcon from '@mui/icons-material/Policy';
import GavelIcon from '@mui/icons-material/Gavel';
import CategoryIcon from '@mui/icons-material/Category';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CodeIcon from '@mui/icons-material/Code';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import authService from '../../api/authService';
import { useUserContext } from '../user-context';
import { syncDayjsGlobalLocale } from '../../i18n/muiAdmin';
import { t } from '../../i18n/t';

const Dashboard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setUser } = useUserContext();
    const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
    const [hasSubordinates, setHasSubordinates] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    useEffect(() => {
        authService
            .getAccessContext()
            .then((res) => {
                setIsAdmin(!!res?.data?.isAdmin);
                setHasSubordinates(!!res?.data?.hasSubordinates);
            })
            .catch(() => {
                setIsAdmin(false);
                setHasSubordinates(false);
            });
    }, []);

    useEffect(() => {
        syncDayjsGlobalLocale();
    }, []);

    const navItems = useMemo(
        () => [
            { to: '/dashboard', label: t('dashboard.nav.overview'), icon: <HomeIcon /> },
            { to: '/dashboard/users', label: t('dashboard.nav.users'), icon: <PeopleIcon /> },
            { to: '/dashboard/prompts', label: t('dashboard.nav.prompts'), icon: <ChatBubbleIcon /> },
            { to: '/dashboard/test-configs', label: t('dashboard.nav.testConfigs'), icon: <SettingsIcon /> },
            { to: '/dashboard/sensecap', label: t('dashboard.nav.sensecap'), icon: <SensorsIcon /> },
            { to: '/dashboard/device-sensors', label: t('dashboard.nav.deviceSensors'), icon: <MemoryIcon /> },
            { to: '/dashboard/posture-instructions', label: t('dashboard.nav.postureInstructions'), icon: <AccessibilityNewIcon /> },
            { to: '/dashboard/media-files', label: t('dashboard.nav.mediaFiles'), icon: <ImageIcon /> },
            { to: '/dashboard/test-metrics', label: t('dashboard.nav.testMetrics'), icon: <BarChartIcon /> },
            { to: '/dashboard/disclaimers', label: t('dashboard.nav.disclaimers'), icon: <PolicyIcon /> },
            { to: '/dashboard/legal-documents', label: t('dashboard.nav.legalDocuments'), icon: <GavelIcon /> },
            { to: '/dashboard/test-categories', label: t('dashboard.nav.testCategories'), icon: <CategoryIcon /> },
            { to: '/dashboard/entitlements', label: t('dashboard.nav.entitlements'), icon: <VpnKeyIcon /> },
            { to: '/dashboard/questionnaires', label: t('dashboard.nav.questionnaires'), icon: <AssignmentIcon /> },
            { to: '/dashboard/test-scripts', label: t('dashboard.nav.testScripts'), icon: <CodeIcon /> },
        ],
        [],
    );

    const items = useMemo(() => {
        if (isAdmin === null) return [];
        if (isAdmin) return navItems;
        if (hasSubordinates) return navItems.slice(0, 2); // Overview + Users
        return navItems.slice(0, 1); // Overview only
    }, [isAdmin, hasSubordinates, navItems]);

    const handleLogout = () => {
        setUser(null);
        localStorage.removeItem('access_token');
        navigate('/login');
    };

    const sidebarWidth = sidebarOpen ? 220 : 64;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
            <Box
                component="nav"
                sx={{
                    width: sidebarWidth,
                    flexShrink: 0,
                    borderRight: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'width 0.2s',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'flex-end' : 'center', p: 0.5 }}>
                    <Tooltip title={sidebarOpen ? t('dashboard.collapseSidebar') : t('dashboard.expandSidebar')}>
                        <IconButton size="small" onClick={() => setSidebarOpen((o) => !o)}>
                            {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
                <List dense sx={{ flex: 1 }}>
                    {items.map((item) => (
                        <Tooltip key={item.to} title={!sidebarOpen ? item.label : ''} placement="right">
                            <ListItemButton
                                component={NavLink}
                                to={item.to}
                                selected={location.pathname === item.to || (item.to !== '/dashboard' && location.pathname.startsWith(item.to + '/'))}
                                sx={{ py: 1.25, justifyContent: sidebarOpen ? 'flex-start' : 'center' }}
                            >
                                <ListItemIcon sx={{ minWidth: sidebarOpen ? 40 : 0, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                                {sidebarOpen && <ListItemText primary={item.label} />}
                            </ListItemButton>
                        </Tooltip>
                    ))}
                    <Tooltip title={!sidebarOpen ? t('dashboard.logout') : ''} placement="right">
                        <ListItemButton
                            onClick={handleLogout}
                            sx={{ py: 1.25, justifyContent: sidebarOpen ? 'flex-start' : 'center' }}
                        >
                            <ListItemIcon sx={{ minWidth: sidebarOpen ? 40 : 0, justifyContent: 'center' }}><LogoutIcon /></ListItemIcon>
                            {sidebarOpen && <ListItemText primary={t('dashboard.logout')} />}
                        </ListItemButton>
                    </Tooltip>
                </List>
            </Box>
            <Container
                maxWidth={false}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: 0,
                    paddingTop: 2,
                    paddingBottom: 2,
                    overflowX: 'hidden',
                }}
            >
                <Outlet />
            </Container>
        </Box>
    );
};

export default Dashboard;
